import type { CssVarsThemeOptions } from "@mui/material/styles";
import {
  COMFORTABLE_DENSITY_FACTOR,
  COMPACT_DENSITY_FACTOR,
} from "@mui/x-data-grid/hooks/features/density/useGridDensity";
import { gridClasses } from "@mui/x-data-grid-pro";
// Augment the Material UI theme types with the MUI X Data Grid Pro types:
// https://mui.com/x/react-data-grid/getting-started/#typescript
import type {} from "@mui/x-data-grid-pro/themeAugmentation";

/**
 * Aura Material UI theme options object extension to support the
 * [MUI X](https://mui.com/x)
 * [Data Grid Pro](https://mui.com/x/api/data-grid/data-grid-pro).
 *
 * It uses the Material UI experimental
 * [CSS theme variables](https://mui.com/material-ui/experimental-api/css-theme-variables)
 * system, which requires using from `@mui/material/styles` the function
 * `extendTheme` to create the theme (extending from
 * `@tricentis/aura/constants/themeOptions.js` the Aura Material UI theme
 * options object), and the React component `CssVarsProvider` to provide the
 * theme for the entire app.
 * @example
 * How to setup a themed app:
 *
 * ```tsx
 * import ScopedCssBaseline from "@mui/material/ScopedCssBaseline";
 * import {
 *   Experimental_CssVarsProvider as CssVarsProvider,
 *   experimental_extendTheme as extendTheme,
 * } from "@mui/material/styles";
 * import { LicenseInfo } from "@mui/x-license-pro";
 * import muiXLicenseKey from "@tricentis/aura/constants/muiXLicenseKey.js";
 * import themeOptions from "@tricentis/aura/constants/themeOptions.js";
 * import themeOptionsDataGrid from "@tricentis/aura/constants/themeOptionsDataGrid.js";
 *
 * // Set the MUI X Data Grid Pro license key:
 * // https://mui.com/x/introduction/licensing/#how-to-install-the-key
 * LicenseInfo.setLicenseKey(muiXLicenseKey);
 *
 * const theme = extendTheme(themeOptions, themeOptionsDataGrid);
 * const app = (
 *   <CssVarsProvider theme={theme}>
 *     <ScopedCssBaseline>Your app…</ScopedCssBaseline>
 *   </CssVarsProvider>
 * );
 * ```
 */
const themeOptionsDataGrid = {
  components: {
    MuiDataGrid: {
      defaultProps: {
        getRowHeight({ densityFactor }) {
          switch (densityFactor) {
            case COMPACT_DENSITY_FACTOR:
              return 40;
            case COMFORTABLE_DENSITY_FACTOR:
              return 56;
            default:
              return 48;
          }
        },
      },
      styleOverrides: {
        cell: ({ theme }) => ({
          [`&.${gridClasses["cell--editing"]}`]: {
            outlineOffset: -1,
            outlineWidth: 1,
            outlineStyle: "solid",
            outlineColor: theme.vars.palette.info.light,
            "&:focus-within": {
              outlineColor: theme.vars.palette.info.main,
            },
          },
        }),
        row: ({ theme }) => ({
          "&.Mui-selected": {
            backgroundColor: `rgba(${theme.vars.palette.primary.lightChannel} / ${theme.vars.palette.action.selectedOpacity})`,
          },
        }),
      },
    },
  },
} as const satisfies Partial<CssVarsThemeOptions>;

export default themeOptionsDataGrid;
